// src/encounter/intelligence/next-step.ts
//
// The Next step card's payload, `scrips.intelligence.next-step/1`, as the engine sends it from
// POST /api/intelligence/cards/next-step (scrips-clinician-demo at 13df5c0). Code-built by a Scrips rule; no model.
// This module is pure: it checks a payload when it arrives (A2: versioned, bounded; a malformed payload is the
// technical line, never the body) and holds the fixed, non-clinical words the card uses. Every clinical string on
// the card is a payload field verbatim: the app writes no clinical sentence. Nothing here logs.
//
// It also holds the physician decision contract, POST /api/intelligence/decisions: the request body and what each
// reply becomes. A decision is an audit record only; nothing is written to the chart.

export const NEXT_STEP_SCHEMA = 'scrips.intelligence.next-step/1' as const;

export const DECISION_ACTIONS = ['add-to-note', 'not-indicated', 'defer', 'already-done'] as const;
export type DecisionAction = (typeof DECISION_ACTIONS)[number];

/** The button words for each action (the engine contract's labels). */
export const ACTION_LABEL: Record<DecisionAction, string> = {
  'add-to-note': 'Add to note',
  'not-indicated': 'Not indicated',
  defer: 'Defer',
  'already-done': 'Already done',
};

export const NO_EVIDENCE_LINE = 'No evidence linked' as const;

export interface Evidence {
  title: string;
  journal: string;
  year: string;
  pmid?: string;
  doi?: string;
  link: string;
  checked?: string;
  newerEdition?: string;
}

export interface NextStepItem {
  id: string;
  kind: string;
  title: string;
  stateId: string;
  evidence: Evidence | null;
  evidenceLine: typeof NO_EVIDENCE_LINE | null;
  reviewLine: string;
  actions: DecisionAction[];
}

export interface NextStepV1 {
  schema: typeof NEXT_STEP_SCHEMA;
  origin: { kind: 'scrips-rule'; label: string };
  readLine: string;
  notCheckedLine: string;
  items: NextStepItem[];
  /** Engine 0c27ce4: the engine's own line when it has no item (drawn verbatim in place of the list). */
  emptyLine?: string;
  asOf: string;
  auditId: string;
  /** Engine FM rows (INTELLIGENCE_FM_ROWS): how to draw a test the card lists, each with its verbatim source. */
  sequencing?: SequencingLine[];
}

export interface SequencingLine {
  id: string;
  text: string;
  source: { id: string; quote: string; checkedOn: string };
  /** The record items the line rests on (a statin start for sq-lipid-recheck); empty when it rests on the listed test. */
  cites: string[];
}

const MAX_SEQUENCING = 10;
function parseSequencing(raw: unknown): SequencingLine[] | null {
  if (!Array.isArray(raw) || raw.length > MAX_SEQUENCING) return null;
  const out: SequencingLine[] = [];
  for (const x of raw) {
    if (!isObj(x) || !str(x.id, 64) || !str(x.text, 400) || !isObj(x.source)) return null;
    const s = x.source;
    if (!str(s.id, 64) || !str(s.quote, 2000) || !str(s.checkedOn, 40)) return null;
    const cites = x.cites ?? [];
    if (!Array.isArray(cites) || cites.length > 50 || !cites.every((c) => str(c, 200))) return null;
    out.push({ id: x.id, text: x.text, source: { id: s.id, quote: s.quote, checkedOn: s.checkedOn }, cites: cites as string[] });
  }
  return out;
}

/** The cards a decision may be recorded against. Only 'next-step' sends today. */
export type DecisionCard = 'next-step' | 'explain';

export interface DecisionRequest {
  decisionId: string;
  patientId: string;
  encounterId: string | null;
  card: DecisionCard;
  itemId: string;
  action: DecisionAction;
  reason?: string;
  decidedAt: string;
}

/** Fixed decision lines (engine contract wording). */
export const DECISION_LINE = {
  conflict: 'Already recorded differently. Reload the card.',
  auditDown: 'Not recorded: the audit log is unavailable. Try again.',
  technical: 'Not recorded. Try again.',
  refused: 'Scrips Intelligence is not enabled for this patient.',
  recorded: (a: DecisionAction) => `Recorded: ${ACTION_LABEL[a]}`,
} as const;

/** The engine's itemId rule; a decision can only name an item id of this shape. */
export const ITEM_ID = /^[A-Za-z0-9:._-]{1,160}$/;
/** "Not indicated" needs one plain phrase of at most this many characters. */
export const REASON_MAX = 200;

export interface RecordedDecision {
  decisionId: string;
  card: string;
  itemId: string;
  action: DecisionAction;
  reason?: string;
  decidedAt: string;
  encounterId: string | null;
}

// ── Payload check (A2) ───────────────────────────────────────────────────────────────────────────────────────
const MAX_ITEMS = 50;
const MAX_TEXT = 2000;

const str = (v: unknown, max = MAX_TEXT): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;
const optStr = (v: unknown): v is string | undefined => v === undefined || str(v);
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Only an https link is drawn as a link; anything else (javascript:, data:, http:) makes the payload malformed. */
const httpsLink = (v: unknown): v is string => {
  if (!str(v)) return false;
  try {
    return new URL(v).protocol === 'https:';
  } catch {
    return false;
  }
};

function parseEvidence(raw: unknown): Evidence | null | undefined {
  if (raw === null) return null;
  if (!isObj(raw)) return undefined;
  const year = typeof raw.year === 'number' && Number.isInteger(raw.year) ? String(raw.year) : raw.year;
  if (!str(raw.title) || !str(raw.journal) || !str(year, 10) || !httpsLink(raw.link)) return undefined;
  if (!optStr(raw.pmid) || !optStr(raw.doi) || !optStr(raw.newerEdition)) return undefined;
  if (raw.checked !== undefined && typeof raw.checked !== 'string' && typeof raw.checked !== 'boolean') return undefined;
  const e: Evidence = { title: raw.title, journal: raw.journal, year, link: raw.link };
  if (raw.pmid) e.pmid = raw.pmid;
  if (raw.doi) e.doi = raw.doi;
  if (raw.newerEdition) e.newerEdition = raw.newerEdition;
  return e;
}

function parseItem(raw: unknown): NextStepItem | null {
  if (!isObj(raw)) return null;
  if (typeof raw.id !== 'string' || !ITEM_ID.test(raw.id) || !str(raw.kind, 200) || !str(raw.title) || !str(raw.stateId, 200) || !str(raw.reviewLine)) return null;
  const evidence = parseEvidence(raw.evidence);
  if (evidence === undefined) return null;
  const evidenceLine = raw.evidenceLine ?? null;
  if (evidenceLine !== null && evidenceLine !== NO_EVIDENCE_LINE) return null;
  // Exactly one of: a linked source, or the engine's "No evidence linked".
  if ((evidence === null) === (evidenceLine === null)) return null;
  const actions = raw.actions;
  if (!Array.isArray(actions) || actions.length !== 4) return null;
  if (!actions.every((a) => (DECISION_ACTIONS as readonly unknown[]).includes(a)) || new Set(actions).size !== 4) return null;
  return {
    id: raw.id,
    kind: raw.kind,
    title: raw.title,
    stateId: raw.stateId,
    evidence,
    evidenceLine,
    reviewLine: raw.reviewLine,
    actions: actions as DecisionAction[],
  };
}

/** A2: the checked payload, or `null` for anything malformed (the card then shows the technical line). */
export function parseNextStep(raw: unknown): NextStepV1 | null {
  if (!isObj(raw) || raw.schema !== NEXT_STEP_SCHEMA) return null;
  const o = raw.origin;
  if (!isObj(o) || o.kind !== 'scrips-rule' || !str(o.label, 200)) return null;
  if (!str(raw.readLine) || !str(raw.notCheckedLine) || !str(raw.asOf, 64) || !str(raw.auditId, 200)) return null;
  // Engine 0c27ce4: an optional string; any other type (or an empty string) makes the payload malformed.
  if (!optStr(raw.emptyLine)) return null;
  if (!Array.isArray(raw.items) || raw.items.length > MAX_ITEMS) return null;
  const items: NextStepItem[] = [];
  for (const it of raw.items) {
    const p = parseItem(it);
    if (!p) return null;
    items.push(p);
  }
  if (new Set(items.map((i) => i.id)).size !== items.length) return null;
  // Optional; a malformed list is the technical line like any other field (A2), never drawn in part.
  const sequencing = raw.sequencing === undefined ? undefined : parseSequencing(raw.sequencing);
  if (sequencing === null) return null;
  return {
    schema: NEXT_STEP_SCHEMA,
    origin: { kind: 'scrips-rule', label: o.label },
    readLine: raw.readLine,
    notCheckedLine: raw.notCheckedLine,
    items,
    ...(raw.emptyLine !== undefined ? { emptyLine: raw.emptyLine } : {}),
    asOf: raw.asOf,
    auditId: raw.auditId,
    ...(sequencing?.length ? { sequencing } : {}),
  };
}

/** The decision reply: true only for a 200 that echoes the id that was sent. */
export function isRecorded(body: unknown, decisionId: string): boolean {
  return isObj(body) && body.recorded === true && body.decisionId === decisionId;
}

/** GET /api/intelligence/decisions?patientId= reply, newest first. Null when malformed (then nothing is pre-filled). */
export function parseDecisions(raw: unknown): RecordedDecision[] | null {
  if (!isObj(raw) || !Array.isArray(raw.decisions) || raw.decisions.length > 1000) return null;
  const out: RecordedDecision[] = [];
  for (const d of raw.decisions) {
    if (!isObj(d) || !str(d.decisionId, 64) || !str(d.card, 64) || typeof d.itemId !== 'string' || !ITEM_ID.test(d.itemId)) return null;
    if (!(DECISION_ACTIONS as readonly unknown[]).includes(d.action) || !str(d.decidedAt, 64)) return null;
    if (d.encounterId !== null && d.encounterId !== undefined && typeof d.encounterId !== 'string') return null;
    out.push({ decisionId: d.decisionId, card: d.card, itemId: d.itemId, action: d.action as DecisionAction, ...(typeof d.reason === 'string' ? { reason: d.reason } : {}), decidedAt: d.decidedAt, encounterId: (d.encounterId as string | null | undefined) ?? null });
  }
  return out;
}
